/**
 * Deep-linking between Home widgets and the bookings list. Home tiles resolve a
 * set of `BookingsFilters` (e.g. from a queue preset) and link to
 * `/bookings?…`; the bookings page hydrates its property-filter bar from those
 * params whenever they appear (including a click while already on the list)
 * so the same slice is shown and remains editable.
 */
import {
  consolidateMultiValueTokens,
  EMPTY_PROPERTY_FILTER_QUERY,
  newTokenId,
  type FilterOperator,
  type PropertyFilterQuery,
  type PropertyFilterToken,
} from "@/components/table";
import { BOOKING_FILTER_PROPERTIES } from "@/features/bookings/bookingFilterProperties";
import { tableSortFromReport } from "@/features/bookings/report/bookingLabels";
import { splitLaneKey } from "@/lib/formatPort";
import { decodeFilterPayload } from "@/lib/filterEncoding";
import type { BookingsFilters } from "@/types/api";

/** Params carried in the URL but not represented as property-filter tokens. */
const NON_TOKEN_KEYS = new Set([
  "page",
  "per_page",
  "sort_by",
  "sort_dir",
  "filter_operation",
  "linked_scope",
  "f",
  "group_by",
  "favorites_only",
  "report_columns",
]);

/** Date columns the bookings table can send as `sort_by`. */
export const BOOKING_DATE_SORT_KEYS = [
  "departure_date",
  "arrival_date",
  "open_date",
  "cutoff_date",
  "effective_cutoff",
  "stuffing_date",
  "ready_date",
  "snapshot_date",
  "pcd",
  "effective_pcd",
] as const;

export type BookingsSortDir = "asc" | "desc";

export interface BookingsSort {
  sort_by?: string;
  sort_dir?: BookingsSortDir;
}

/** Read table sort from a `/bookings?…` deep link (Home queue presets). */
export function parseBookingsSort(params: URLSearchParams): BookingsSort {
  const rawSort = params.get("sort_by")?.trim() || undefined;
  const fromReport = rawSort ? tableSortFromReport(rawSort) : undefined;
  const sortBy =
    fromReport ??
    (rawSort &&
    (BOOKING_DATE_SORT_KEYS as readonly string[]).includes(rawSort)
      ? rawSort
      : undefined);
  const dir = params.get("sort_dir")?.trim().toLowerCase();
  return {
    sort_by: sortBy,
    sort_dir: dir === "asc" || dir === "desc" ? dir : undefined,
  };
}

export function hasBookingsSortParams(params: URLSearchParams): boolean {
  const parsed = parseBookingsSort(params);
  return Boolean(parsed.sort_by || parsed.sort_dir);
}

/** Plain text/enum keys whose value round-trips as one token per comma value. */
const TEXT_KEYS = [
  "state",
  "carrier",
  "agent",
  "origin_port",
  "destination_port",
  "contract_ref",
  "shipper",
  "consignee",
  "sales_person",
  "opened_by",
  "week",
  "load_type",
  "equipment_size",
  "equipment_type",
  "tag",
  "delay_source",
  "eta_delay_source",
  "rebook_from_contract",
  "rebook_to_contract",
  // Commercial opportunity subset (rebook / first_book). Surfaced as a visible,
  // editable property-filter chip on the bookings list; commercial digest
  // show-more links carry it so clicking narrows opportunities to the subset.
  "opportunity_kind",
  "service_trade",
  "source_id",
  "operational_flow",
  "operational_flow_port",
] as const;

/** Date property → the API `_from` / `_to` param pair it maps to. */
const DATE_PROPERTY_PARAMS: Record<string, { from: string; to: string }> = {
  etd: { from: "departure_from", to: "departure_to" },
  eta: { from: "eta_from", to: "eta_to" },
  pcd: { from: "pcd_from", to: "pcd_to" },
  cutoff: { from: "cutoff_from", to: "cutoff_to" },
  stuffing: { from: "stuffing_from", to: "stuffing_to" },
  ready_date: { from: "ready_date_from", to: "ready_date_to" },
  open_date: { from: "open_date_from", to: "open_date_to" },
  last_update: { from: "last_updated_from", to: "last_updated_to" },
  operational_flow_date: {
    from: "operational_flow_from",
    to: "operational_flow_to",
  },
};

/** Number property → the API gte/lte/eq param trio it maps to. */
const NUMBER_PROPERTY_PARAMS: Record<
  string,
  { gte: string; lte: string; eq: string }
> = {
  weight: { gte: "weight_gte", lte: "weight_lte", eq: "weight_eq" },
  delay_days: {
    gte: "delay_days_gte",
    lte: "delay_days_lte",
    eq: "delay_days_eq",
  },
  eta_delay_days: {
    gte: "eta_delay_days_gte",
    lte: "eta_delay_days_lte",
    eq: "eta_delay_days_eq",
  },
  total_containers: {
    gte: "total_containers_gte",
    lte: "total_containers_lte",
    eq: "total_containers_eq",
  },
};

/** Build a `/bookings?…` URL from resolved filters (page/per_page omitted). */
export function buildBookingsFilterUrl(filters: BookingsFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === "") continue;
    if (key === "page" || key === "per_page") continue;
    params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `/bookings?${query}` : "/bookings";
}

function token(
  propertyKey: string,
  operator: FilterOperator,
  value: string,
): PropertyFilterToken {
  return {
    id: newTokenId(),
    propertyKey,
    operator,
    value,
    valueLabel: value,
  };
}

function pushCsv(
  tokens: PropertyFilterToken[],
  propertyKey: string,
  operator: FilterOperator,
  csv: string,
) {
  const values = csv
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (values.length === 0) return;
  tokens.push({
    id: newTokenId(),
    propertyKey,
    operator,
    value: values.join(","),
    valueLabel: values.join(", "),
  });
}

/**
 * Convert a URL search-params blob (bookings filter shape) into a property
 * filter query the bookings page can apply. Sorting/pagination are ignored.
 * Returns the empty query when no recognised filters are present.
 */
export function searchParamsToBookingsQuery(
  params: URLSearchParams,
): PropertyFilterQuery {
  const tokens: PropertyFilterToken[] = [];

  for (const key of TEXT_KEYS) {
    const value = params.get(key);
    const operator: FilterOperator =
      key === "state" || key === "service_trade" ? ":" : "=";
    if (value) pushCsv(tokens, key, operator, value);
    const negated = params.get(`${key}_not`);
    if (negated) {
      pushCsv(tokens, key, key === "service_trade" ? "!:" : "!=", negated);
    }
  }

  const lane = params.get("lane") ?? params.get("lane_key");
  if (lane) {
    const parts = splitLaneKey(lane);
    if (parts) {
      if (!params.get("origin_port")) {
        tokens.push(token("origin_port", "=", parts.origin));
      }
      if (!params.get("destination_port")) {
        tokens.push(token("destination_port", "=", parts.destination));
      }
    }
  }

  for (const [property, pair] of Object.entries(DATE_PROPERTY_PARAMS)) {
    const from = params.get(pair.from);
    if (from) tokens.push(token(property, ">=", from));
    const to = params.get(pair.to);
    if (to) tokens.push(token(property, "<=", to));
  }

  for (const [property, trio] of Object.entries(NUMBER_PROPERTY_PARAMS)) {
    const gte = params.get(trio.gte);
    if (gte) tokens.push(token(property, ">=", gte));
    const lte = params.get(trio.lte);
    if (lte) tokens.push(token(property, "<=", lte));
    const eq = params.get(trio.eq);
    if (eq) tokens.push(token(property, "=", eq));
  }

  const search = params.get("search");
  if (search) {
    tokens.push({
      id: newTokenId(),
      operator: ":",
      value: search,
      valueLabel: search,
    });
  }

  if (tokens.length === 0) return EMPTY_PROPERTY_FILTER_QUERY;

  return consolidateMultiValueTokens(
    {
      operation: params.get("filter_operation") === "or" ? "or" : "and",
      tokens,
    },
    BOOKING_FILTER_PROPERTIES,
  );
}

/** Decode booking-report email / schedule `?f=` payloads into a property query. */
export function propertyFilterQueryFromEncodedF(
  encoded: string,
): PropertyFilterQuery | null {
  const obj = decodeFilterPayload<{
    o?: "and" | "or";
    t?: { k?: string; op?: string; v: unknown }[];
  }>(encoded);
  if (!obj?.t?.length) return null;
  const tokens: PropertyFilterToken[] = [];
  for (const item of obj.t) {
    if (typeof item.v !== "string" || item.v.trim() === "") continue;
    tokens.push({
      id: newTokenId(),
      propertyKey: item.k || undefined,
      operator: (item.op as FilterOperator) ?? ":",
      value: item.v,
      valueLabel: item.v,
    });
  }
  if (tokens.length === 0) return null;
  return consolidateMultiValueTokens(
    { operation: obj.o === "or" ? "or" : "and", tokens },
    BOOKING_FILTER_PROPERTIES,
  );
}

/** Whether the search params carry any recognised bookings filter. */
export function hasBookingsFilterParams(params: URLSearchParams): boolean {
  if (params.get("f")?.trim()) return true;
  for (const key of params.keys()) {
    if (NON_TOKEN_KEYS.has(key)) continue;
    return true;
  }
  return false;
}

export function favoritesOnlyFromParams(params: URLSearchParams): boolean {
  const value = params.get("favorites_only")?.trim().toLowerCase();
  return value === "yes" || value === "true" || value === "1";
}

export function reportColumnsFromParams(params: URLSearchParams): string[] | null {
  const raw = params.get("report_columns")?.trim();
  if (!raw) return null;
  const cols = raw
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  return cols.length > 0 ? cols : null;
}

export function groupByFromParams(params: URLSearchParams): string | undefined {
  const value = params.get("group_by")?.trim();
  return value || undefined;
}

/** Layout / filter params from a booking-report view link (not flat API filters). */
export function hasBookingsReportViewParams(params: URLSearchParams): boolean {
  return (
    Boolean(params.get("f")?.trim()) ||
    Boolean(params.get("report_columns")?.trim()) ||
    Boolean(params.get("group_by")?.trim()) ||
    favoritesOnlyFromParams(params)
  );
}

/** Whether a deep link requests the opportunities-only bookings slice. */
export function opportunitiesOnlyFromParams(params: URLSearchParams): boolean {
  const value = params.get("has_opportunity")?.trim().toLowerCase();
  if (value === "yes" || value === "true" || value === "1") return true;
  // A commercial opportunity-kind link (rebook / first_book) is a subset of
  // the opportunities-only slice, so it implies the toggle too.
  return opportunityKindFromParams(params) !== null;
}

/**
 * The commercial opportunity kind requested by a deep link, if any. Mirrors the
 * API `opportunity_kind` filter used by commercial digest show-more links.
 */
export function opportunityKindFromParams(
  params: URLSearchParams,
): "rebook" | "first_book" | "consolidation" | null {
  const value = params.get("opportunity_kind")?.trim().toLowerCase();
  return value === "rebook" ||
    value === "first_book" ||
    value === "consolidation"
    ? value
    : null;
}

export interface BookingsUrlHydration {
  query: PropertyFilterQuery;
  showOpportunitiesOnly: boolean;
  /**
   * Commercial opportunity kind (`rebook` / `first_book`) or `null`. The kind is
   * ALSO emitted as an `opportunity_kind` token inside {@link query} (so it
   * shows as an editable property-filter chip); this field is retained so a
   * kind link can imply the opportunities toggle.
   */
  opportunityKind: "rebook" | "first_book" | "consolidation" | null;
}

/**
 * Parse `/bookings?…` params into property-filter tokens (which now include the
 * `opportunity_kind` subset chip) plus the dedicated opportunities toggle
 * (`has_opportunity`, kept out of the property filter bar).
 */
export function parseBookingsUrlParams(
  params: URLSearchParams,
): BookingsUrlHydration {
  const encodedF = params.get("f")?.trim();
  const fromF = encodedF ? propertyFilterQueryFromEncodedF(encodedF) : null;
  const fromFlat = searchParamsToBookingsQuery(params);
  const query =
    fromF && fromF.tokens.length > 0
      ? fromF
      : fromFlat.tokens.length > 0
        ? fromFlat
        : fromF ?? fromFlat;
  return {
    query,
    showOpportunitiesOnly: opportunitiesOnlyFromParams(params),
    opportunityKind: opportunityKindFromParams(params),
  };
}
